import { forwardRef } from 'react'

import { RiCloseCircleLine, RiErrorWarningLine, RiInformation2Line } from '@remixicon/react'
import { type VariantProps, cva } from 'class-variance-authority'

import { cn } from '../utils'

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        default: 'bg-background-contrast/40 text-foreground border-border-secondary/50 ',
        destructive:
          'border-destructive/50 bg-destructive/40 text-destructive-foreground border-destructive/50',
        information:
          'border-spectrum-amber-subtle text-spectrum-amber-light bg-spectrum-amber-subtle',
        warning: 'border-warning/50 text-warning-foreground bg-warning/40',
      },
    },
  }
)

export type TAlertVariant = VariantProps<typeof alertVariants>

const variantIcons: Partial<Record<NonNullable<TAlertVariant['variant']>, React.ElementType>> = {
  default: undefined,
  destructive: RiCloseCircleLine,
  information: RiInformation2Line,
  warning: RiErrorWarningLine,
}

const Alert = forwardRef<
  HTMLDivElement,
  {
    icon?: React.ElementType
  } & React.HTMLAttributes<HTMLDivElement> &
    TAlertVariant
>(({ children, className, icon, variant, ...props }, ref) => {
  const Icon = icon ?? variantIcons[variant || 'default']

  return (
    <div className={cn(alertVariants({ variant }), className)} ref={ref} role="alert" {...props}>
      <div className="flex flex-row items-center gap-2 has-[.alert-description]:items-start">
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <div className="flex flex-col gap-1">{children}</div>
      </div>
    </div>
  )
})
Alert.displayName = 'Alert'

const AlertTitle = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      className={cn('alert-title font-medium leading-none tracking-tight', className)}
      ref={ref}
      {...props}
    />
  )
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn('alert-description text-sm [&_p]:leading-relaxed', className)}
    ref={ref}
    {...props}
  />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertDescription, AlertTitle }
